<template>
    <div>
        <div class="action-header">
            <div class="search-container">
                <div class="search-input">
                    <v-text-field
                        v-model="search"
                        prepend-inner-icon="search"
                        :placeholder="this.$vuetify.lang.t('$vuetify.blacklist.searchByName')"
                        solo
                        height="40"
                        @keyup.enter="getBlacklistPagination()">
                    </v-text-field>
                </div>

                <div class="ml-3">
                    <v-btn
                        @click="searchBlacklist()"
                        color="primary"
                        outlined
                        height="40">
                        {{ $vuetify.lang.t('$vuetify.blacklist.search') }}
                    </v-btn>
                </div>
            </div>
            <div>
                <v-btn
                    v-if="permissionCreate"
                    height="40px"
                    color="primary"
                    @click="createBlacklist()">
                    + {{ $vuetify.lang.t('$vuetify.blacklist.create') }}
                </v-btn>
            </div>
        </div>

        <v-card class="content-container">
            <v-data-table
                :footer-props="{
                    'items-per-page-options': [10, 20, 30, 50, 100]
                }"
                @page-count="pageCount = $event"
                :page.sync="page"
                :headers="headers"
                :options.sync="options"
                :server-items-length="total"
                :items="blacklists"
                :loading="loading">
                <template v-slot:[`item.fullname`]="{ item }">
                    <span>
                        <v-avatar class="mr-3">
                            <img :src="getBlacklistImage(item.image)">
                        </v-avatar>
                        {{ item.fullname }}
                    </span>
                </template>
                <template v-slot:[`item.action`]="{ item }">
                    <div class="action">
                        <v-icon
                            v-if="permissionUpdate"
                            class="mr-2"
                            @click.stop="updateBlacklist(item.id)">
                            edit
                        </v-icon>
                        <v-icon
                            v-if="permissionDelete"
                            class="mr-2"
                            @click.stop="openDeleteBlacklistModal(item)">
                            delete
                        </v-icon>
                    </div>
                </template>
            </v-data-table>
        </v-card>
        <div class="pt-2 paginator-box">
            <div>
                <v-pagination v-model="page" :length="pageCount"></v-pagination>
            </div>
        </div>

        <delete-modal
            ref="modalBlacklist"
            @success-deleted="deleteSuccess()"
            :deleteForm="deleteForm"/>
    </div>
</template>

<script>
import DeleteModal from '@/components/modal/DeleteModal'
import permissionConstant from '@/constants/permissions.js'
import profileDefault from '@/assets/user_default.png'
export default {
    name: 'Blacklist',

    components: {
        DeleteModal
    },

    data () {
        return {
            search: '',
            pageCount: 1,
            page: 1,
            options: {},
            loading: false,
            total: 0,
            blacklists: [],
            permissionCreate: true,
            permissionUpdate: true,
            permissionDelete: true,
            deleteForm: {
                header: '',
                content: '',
                path: ''
            }
        }
    },

    computed: {
        headers () {
            return [
                { text: this.$vuetify.lang.t('$vuetify.blacklist.name'), value: 'fullname', width: '30%' },
                { text: this.$vuetify.lang.t('$vuetify.blacklist.reason'), value: 'reason', width: '35%' },
                { text: this.$vuetify.lang.t('$vuetify.blacklist.createdDate'), value: 'created_at', width: '15%' },
                { text: this.$vuetify.lang.t('$vuetify.blacklist.status'), value: 'status', width: '10%' },
                { text: '', value: 'action', width: '10%', sortable: false }
            ]
        }
    },

    methods: {
        getBlacklistImage (imageLink) {
            if (imageLink) {
                return imageLink
            }

            return profileDefault
        },

        async openDeleteBlacklistModal (blacklist) {
            this.deleteForm.header = this.$vuetify.lang.t('$vuetify.blacklist.deleteBlacklist')
            this.deleteForm.content = `${this.$vuetify.lang.t('$vuetify.blacklist.wantToDelete')} "${blacklist.fullname}"?`
            this.deleteForm.path = `blacklist/delete/${blacklist.id}`
            this.$refs.modalBlacklist.dialog = true
        },

        async deleteSuccess () {
            await this.getBlacklistPagination()
            this.$notify({
                title: this.$vuetify.lang.t('$vuetify.blacklist.congratulation'),
                text: this.$vuetify.lang.t('$vuetify.blacklist.blacklistIsDeleted'),
                type: 'success'
            })
        },

        async searchBlacklist () {
            this.options.page = 1
            await this.getBlacklistPagination()
        },

        async getBlacklistPagination () {
            this.loading = true
            const { sortBy, sortDesc, page, itemsPerPage } = this.options

            let url = `blacklists?page=${page}&itemsPerPage=${itemsPerPage}`

            if (sortBy[0]) {
                url += `&sortBy=${sortBy[0]}`
            }

            if (sortDesc[0]) {
                url += `&sortDesc=${sortDesc[0]}`
            }

            if (this.search) {
                url += `&search=${this.search}`
            }

            const res = await this.axios.get(url)
            if (res && res.data && res.data.status === 'ok') {
                this.blacklists = res.data.data
                this.total = res.data.total
                this.loading = false
            }
        },

        createBlacklist () {
            this.$router.push({ name: 'BlacklistForm' }, () => {})
        },

        updateBlacklist (id) {
            this.$router.push({ name: 'BlacklistForm', params: { id } })
        },

        checkPermission () {
            this.permissionCreate = this.$util.checkPermission(permissionConstant.blacklist.create)
            this.permissionUpdate = this.$util.checkPermission(permissionConstant.blacklist.update)
            this.permissionDelete = this.$util.checkPermission(permissionConstant.blacklist.delete)
        }
    },

    mounted () {
        this.checkPermission()
    },

    watch: {
        options: {
            async handler () {
                await this.getBlacklistPagination()
            },
            deep: true
        },
        '$vuetify.lang.current': {
            handler: async function (after, before) {
                await this.getBlacklistPagination()
            },
            deep: true
        }
    }
}
</script>

<style lang="scss" scoped>
::v-deep .v-input__control > .v-input__slot {
    min-height: auto !important;
    display: flex !important;
    align-items: center !important;
    box-shadow: none !important;
    border: 1px solid #a8a8a8 !important;
}
</style>
